/**
 * CSV/PDF Export Utility — pure-JS CSV generation, PDF generation, and file download.
 *
 * CSV: No dependencies required — builds a CSV Blob and triggers the browser's
 * native download mechanism via a temporary <a> element.
 *
 * PDF: Uses jsPDF + jspdf-autotable for report PDF generation.
 */

import { iAmHigherAuthority, convertIsoUTCToLocal } from '../libs.jsx';
import { hasEhrResidentId } from './session.js';
import { utcToCampusDate } from '../utils/campusTime.js';
import { intercomUsedLabel, intercomAnsweredLabel, timeToConnectCell, TIME_TO_CONNECT_NA }
  from '../pages/reports/alert-details/timeToConnect.js';
import { callOutcomeLabel, callEndReasonLabel, callTalkLength } from '../utils/callOutcome.js';
import { ehrBucket, exportColumns, formatEhrCell } from '../pages/reports/ehr-tasks/ehrTasksColumns.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ---------------------------------------------------------------------------
// Core CSV builder
// ---------------------------------------------------------------------------

/**
 * Convert an array of objects into a CSV string using the given column headers.
 *
 * @param {Array<{label: string, key: string}>} headers — column definitions
 * @param {Array<object>} rows — data rows
 * @returns {string} — CSV-formatted text (UTF-8 with BOM for Excel compat)
 */
function buildCsv(headers, rows) {
  const escape = (val) => {
    const str = val == null ? '' : String(val);
    // Wrap in quotes if the value contains a comma, quote, or newline
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const headerLine = headers.map((h) => escape(h.label)).join(',');
  const dataLines = rows.map((row) =>
    headers.map((h) => escape(row[h.key])).join(',')
  );

  return [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger a browser file download for the given CSV content.
 *
 * @param {string} csvString — raw CSV text
 * @param {string} filename — download filename (e.g. "residents.csv")
 */
function downloadCsv(csvString, filename) {
  // UTF-8 BOM so Excel opens with correct encoding
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

// ---------------------------------------------------------------------------
// Invoices Export (Billing)
// ---------------------------------------------------------------------------

const INVOICE_HEADERS = [
  { label: 'Invoice #', key: 'invoice#' },
  { label: 'Status', key: 'status' },
  { label: 'Invoice Date', key: 'invoice_date' },
  { label: 'Due Date', key: 'due_date' },
  { label: 'Total', key: 'total' },
  { label: 'Balance', key: 'balance' },
];

/**
 * Export invoices to CSV and trigger download.
 *
 * @param {Array<object>} invoices — invoice data
 * @param {string} [filename='invoices.csv'] — download filename
 */
export function exportInvoicesCsv(invoices, filename = 'invoices.csv') {
  const csv = buildCsv(INVOICE_HEADERS, invoices);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wellness Report Export (dynamic columns)
// ---------------------------------------------------------------------------

/**
 * Export wellness report data to CSV using dynamic column definitions.
 *
 * @param {Array<object>} rows — report data (sorted)
 * @param {Array<{key: string, label: string}>} columnDefs — active column definitions
 * @param {string} [filename='wellness-report.csv'] — download filename
 */
export function exportWellnessCsv(rows, columnDefs, filename = 'wellness-report.csv') {
  const headers = columnDefs.map((col) => ({ label: col.label, key: col.key }));
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// EHR Tasks History Export (docs/EHR_TASK_HISTORY_PLAN.md §8)
// ---------------------------------------------------------------------------

const EHR_HISTORY_HEADERS = [
  { label: 'Decided', key: 'decided' },
  { label: 'Task', key: 'task' },
  { label: 'Subject Type', key: 'subject_type' },
  { label: 'Subject', key: 'subject' },
  { label: 'Outcome', key: 'outcome' },
  { label: 'Decided By', key: 'decided_by' },
  { label: 'Received', key: 'received' },
  { label: 'Reason', key: 'reason' },
  { label: 'Changes', key: 'changes' },
  { label: 'Proposed', key: 'proposed' },
];

/**
 * Export the filtered EHR task History. The page passes display-ready rows (campus-local
 * times, labels, and Changes / Proposed already flattened), so the file matches the list.
 *
 * @param {Array<object>} rows — keyed as EHR_HISTORY_HEADERS
 * @param {string} [filename='ehr-task-history.csv']
 */
export function exportEhrTaskHistoryCsv(rows, filename = 'ehr-task-history.csv') {
  downloadCsv(buildCsv(EHR_HISTORY_HEADERS, rows), filename);
}

/**
 * Export wellness report data to PDF using dynamic column definitions.
 *
 * @param {Array<object>} rows — report data (sorted)
 * @param {Array<{key: string, label: string}>} columnDefs — active column definitions
 * @param {string} title — report title
 * @param {string} dateRange — date range display string
 * @param {string} [filename='wellness-report.pdf'] — download filename
 */
export function exportWellnessPdf(rows, columnDefs, title = 'Wellness Report', dateRange = '', filename = 'wellness-report.pdf') {
  const isWide = columnDefs.length > 5;
  const doc = new jsPDF({ orientation: isWide ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });

  const pdfHeaders = columnDefs.map((col) => col.label);
  const pdfRows = rows.map((row) =>
    columnDefs.map((col) => {
      const val = row[col.key];
      if (val == null) return '';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      if (val === 1 || val === '1' || val === true) {
        if (col.key === 'checked_in' || col.key === 'rounds_performed') return 'Yes';
      }
      if (val === 0 || val === '0' || val === false) {
        if (col.key === 'checked_in' || col.key === 'rounds_performed') return 'No';
      }
      return String(val);
    })
  );

  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 10, right: 10 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Residents Export
// ---------------------------------------------------------------------------

/** CSV header map for resident export — keyed by table column key */
const RESIDENT_HEADERS_MAP = {
  resident_first_name: { label: 'First Name', key: 'resident_first_name' },
  resident_last_name: { label: 'Last Name', key: 'resident_last_name' },
  resident_nickname: { label: 'Nickname', key: 'resident_nickname' },
  area_name: { label: 'Area', key: 'area_name' },
  area_type_name: { label: 'Area Type', key: 'area_type_name' },
  resident_groups: { label: 'Groups', key: '_groups' },
  resident_phone: { label: 'Phone', key: 'resident_phone' },
  resident_email: { label: 'Email', key: 'resident_email' },
  ehr_level_of_care: { label: 'Care Level', key: 'ehr_level_of_care' },
  ehr_resident_id: { label: 'EHR ID', key: '_ehr_resident_id' },
  devices: { label: 'Devices', key: '_devices' },
  check_in_time: { label: 'Last Check-in', key: 'check_in_time' },
  move_in_date: { label: 'Move-in Date', key: '_move_in_date' },
  status: { label: 'Status', key: '_status' },
};

/** Column order for resident CSV output */
const RESIDENT_COLUMN_ORDER = [
  'resident_first_name', 'resident_last_name', 'resident_nickname',
  'area_name', 'area_type_name', 'resident_groups', 'resident_phone',
  'resident_email', 'ehr_level_of_care', 'ehr_resident_id', 'devices', 'check_in_time', 'move_in_date', 'status',
];

/** Extra column visible only to higher-authority users (weight > 80) */
const RESIDENT_HEADERS_ADMIN = [
  { label: 'Resident ID', key: 'resident_id' },
];

/**
 * One device as it appears in a Devices export cell: `Name (MANUFACTURE_UUID)`.
 *
 * Shared by the residents and areas exports so the two can't drift apart. This
 * restores what V1 produced (`components/Residents.jsx:400`) — the UUID was lost
 * in the V2 rebuild, and it's the field people actually search on when matching
 * an exported row back to physical hardware.
 *
 * Two edge cases worth keeping:
 *  - No UUID → emit just the name, never a bare `Name ()`.
 *  - No name → fall back to `device_identifier` (some rows carry only that).
 *
 * @param {object} d — device from a nested `devices` array
 * @param {string} [trailing] — appended after the parenthetical (areas pass the
 *   `descriptions` / "Location within area" value, which they showed before)
 * @returns {string}
 */
function formatCsvDevice(d, trailing = '') {
  const name = d.device_name || d.device_identifier || '';
  const uuid = String(d.manufacture_uuid ?? '').trim();
  const labelled = uuid ? `${name} (${uuid})`.trim() : name;
  return `${labelled} ${trailing || ''}`.trim();
}

/** Join a nested devices array into one export cell. */
function joinCsvDevices(devices, trailingFor) {
  return (devices || [])
    .map((d) => formatCsvDevice(d, trailingFor ? trailingFor(d) : ''))
    .filter(Boolean)
    .join(' | ') || '';
}

/**
 * Transform a resident row for CSV export (flatten nested arrays to strings).
 *
 * @param {object} r — raw resident object from API
 * @returns {object} — flat object ready for CSV
 */
function transformResident(r, tzOffset) {
  return {
    ...r,
    _groups: (r.resident_groups || []).map((g) => g.resident_group_name).join(', ') || '',
    _devices: joinCsvDevices(r.devices),
    // The table's rule (ResidentTable, hasEhrResidentId): null, '', '0' and whitespace mean
    // "not synced yet", so they export blank rather than as a 0 that reads like an id.
    _ehr_resident_id: hasEhrResidentId(r) ? String(r.ehr_resident_id).trim() : '',
    _status: r.is_active ? 'Active' : 'Moved Out',
    // Stored UTC; exported as the campus-local date the table shows.
    _move_in_date: utcToCampusDate(r.move_in_date, tzOffset),
  };
}

/**
 * Export residents to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} residents — resident data (filtered or selected subset)
 * @param {string} [filename='residents.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 * @param {number} [tzOffset=0] — campus tz_offset, for the Move-in Date column
 */
export function exportResidentsCsv(residents, filename = 'residents.csv', myRoleWeight = 10, visibleColumns, tzOffset = 0) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...RESIDENT_HEADERS_ADMIN] : [];
  const dataHeaders = RESIDENT_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => RESIDENT_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = residents.map((r) => transformResident(r, tzOffset));
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Groups Export
// ---------------------------------------------------------------------------

/** CSV column headers for group export (matches V1 layout) */
const GROUP_HEADERS = [
  { label: 'Group', key: 'resident_group_name' },
  { label: 'Category', key: 'resident_group_category_name' },
  { label: 'Description', key: 'resident_description' },
  { label: 'Residents', key: '_residents' },
];

/** Extra column visible only to higher-authority users (weight > 80) */
const GROUP_HEADERS_ADMIN = [
  { label: 'Group ID', key: 'resident_group_id' },
];

/**
 * Transform a group row for CSV export.
 *
 * @param {object} g — enriched group object (has `members` array)
 * @returns {object} — flat object ready for CSV
 */
function transformGroup(g) {
  return {
    ...g,
    _residents: (g.members || []).map((m) => m.resident_name).join(', ') || '',
  };
}

/**
 * Export groups to CSV and trigger download.
 *
 * @param {Array<object>} groups — group data (filtered or selected subset)
 * @param {string} [filename='resident-groups.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 */
export function exportGroupsCsv(groups, filename = 'resident-groups.csv', myRoleWeight = 10) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...GROUP_HEADERS_ADMIN] : [];
  const headers = [...adminHeaders, ...GROUP_HEADERS];
  const rows = groups.map(transformGroup);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Areas Export
// ---------------------------------------------------------------------------

/** CSV header map for area export — keyed by table column key */
const AREA_HEADERS_MAP = {
  area_name: { label: 'Area Name', key: 'area_name' },
  area_type_description: { label: 'Area Type', key: 'area_type_description' },
  area_group_name: { label: 'Area Groups', key: '_area_groups' },
  building_name: { label: 'Building', key: 'building_name' },
  max_occupancy: { label: 'Max Occupancy', key: 'max_occupancy' },
  residents: { label: 'Residents', key: '_residents' },
  devices: { label: 'Devices', key: '_devices' },
};

/** Column order for area CSV output */
const AREA_COLUMN_ORDER = [
  'area_name', 'area_type_description', 'area_group_name', 'building_name',
  'max_occupancy', 'residents', 'devices',
];

/** Extra column visible only to higher-authority users (weight > 80) */
const AREA_HEADERS_ADMIN = [
  { label: 'Area ID', key: 'area_id' },
];

/**
 * Transform an area row for CSV export.
 */
function transformArea(a) {
  return {
    ...a,
    _area_groups: (a.area_groups || []).map((g) => g.area_group_name).join(', ') || '',
    _residents: (a.residents || []).map((r) => r.resident_name || `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim()).join(', ') || '',
    /* Keeps the trailing `descriptions` ("Location within area") this export
       already showed — the UUID is added, nothing is dropped. */
    _devices: joinCsvDevices(a.devices, (d) => d.descriptions || ''),
  };
}

/**
 * Export areas to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} areas — area data (filtered or selected subset)
 * @param {string} [filename='areas.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportAreasCsv(areas, filename = 'areas.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...AREA_HEADERS_ADMIN] : [];
  const dataHeaders = AREA_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => AREA_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = areas.map(transformArea);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Area Groups Export
// ---------------------------------------------------------------------------

/** CSV column headers for area group export (matches V1 csvAreaGroupHeaders) */
const AREA_GROUP_HEADERS = [
  { label: 'Group Name', key: 'area_group_name' },
  { label: 'Description', key: 'area_group_description' },
  { label: 'Areas', key: '_areas' },
];

/** Extra column visible only to higher-authority users (weight > 80) */
const AREA_GROUP_HEADERS_ADMIN = [
  { label: 'Area Group ID', key: 'area_group_id' },
];

/**
 * Transform an area group row for CSV export.
 */
function transformAreaGroup(g) {
  return {
    ...g,
    _areas: (g.areas || []).map((a) => a.area_name).join(', ') || '',
  };
}

/**
 * Export area groups to CSV and trigger download.
 *
 * @param {Array<object>} areaGroups — area group data
 * @param {string} [filename='area-groups.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 */
export function exportAreaGroupsCsv(areaGroups, filename = 'area-groups.csv', myRoleWeight = 10) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...AREA_GROUP_HEADERS_ADMIN] : [];
  const headers = [...adminHeaders, ...AREA_GROUP_HEADERS];
  const rows = areaGroups.map(transformAreaGroup);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Buildings Export
// ---------------------------------------------------------------------------

/** CSV column headers for building export (matches V1 csvBuildingHeaders) */
const BUILDING_HEADERS = [
  { label: 'Building Name', key: 'building_name' },
  { label: 'Street Address', key: 'street_address' },
  { label: 'City', key: 'city' },
  { label: 'State', key: 'state' },
  { label: 'Zip Code', key: 'zipcode' },
  { label: 'Country', key: 'country' },
  { label: 'Areas', key: '_areas' },
];

/** Extra column visible only to higher-authority users (weight > 80) */
const BUILDING_HEADERS_ADMIN = [
  { label: 'Building ID', key: 'building_id' },
];

/**
 * Transform a building row for CSV export.
 */
function transformBuilding(b) {
  return {
    ...b,
    _areas: (b.areas || []).map((a) => a.area_name).join(', ') || '',
  };
}

/**
 * Export buildings to CSV and trigger download.
 *
 * @param {Array<object>} buildings — building data
 * @param {string} [filename='buildings.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 */
export function exportBuildingsCsv(buildings, filename = 'buildings.csv', myRoleWeight = 10) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...BUILDING_HEADERS_ADMIN] : [];
  const headers = [...adminHeaders, ...BUILDING_HEADERS];
  const rows = buildings.map(transformBuilding);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Users Export
// ---------------------------------------------------------------------------

/** CSV column headers for user export (base columns) */
const USER_HEADERS_BASE = [
  { label: 'Username', key: 'username' },
  { label: 'Display Name', key: 'display_name' },
  { label: 'Email', key: 'email' },
  { label: 'Phone', key: 'phone' },
  { label: 'Title', key: 'title' },
  { label: 'Role', key: 'user_role_name' },
  { label: 'Groups', key: 'group_name' },
];

/** Extra columns visible only to higher-authority users (weight > 80) */
const USER_HEADERS_ADMIN = [
  { label: 'User ID', key: 'user_id' },
  { label: 'Cognito ID', key: 'cognito_id' },
];

/** App Status columns — included when is_logged_in is visible on the table */
const USER_HEADERS_LOGIN = [
  { label: 'App Status', key: 'is_logged_in' },
  { label: 'Last Login Time', key: 'last_login_time' },
];

/**
 * Export users to CSV and trigger download.
 * Includes User ID and Cognito ID columns for admins (role_weight > 80).
 * Includes App Status and Last Login Time when the is_logged_in column is visible.
 *
 * @param {Array<object>} users — user data (filtered or selected subset)
 * @param {string} [filename='users.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportUsersCsv(users, filename = 'users.csv', myRoleWeight = 10, visibleColumns) {
  let headers = iAmHigherAuthority(80, myRoleWeight)
    ? [...USER_HEADERS_ADMIN, ...USER_HEADERS_BASE]
    : [...USER_HEADERS_BASE];

  if (visibleColumns && visibleColumns.includes('is_logged_in')) {
    headers = [...headers, ...USER_HEADERS_LOGIN];
  }

  // Map is_logged_in numeric to readable label for CSV output
  const rows = users.map((u) => ({
    ...u,
    is_logged_in: u.is_logged_in ? 'Online' : 'Offline',
  }));

  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// User Groups Export
// ---------------------------------------------------------------------------

/** All possible CSV column headers for user group export (keyed to table column keys) */
const USER_GROUP_HEADERS_MAP = {
  group_name: { label: 'Group Name', key: 'group_name' },
  capcode: { label: 'Pager Channel', key: 'capcode' },
  user_count: { label: 'Users', key: '_users' },
};

/** Column order for CSV output */
const USER_GROUP_COLUMN_ORDER = ['group_name', 'capcode', 'user_count'];

/** Extra columns visible only to higher-authority users */
const USER_GROUP_HEADERS_ADMIN = [
  { label: 'Group ID', key: 'group_id' },
];

/**
 * Transform a user group row for CSV export.
 */
function transformUserGroup(g) {
  return {
    ...g,
    _users: (g.users || []).map((u) => u.display_name || u.username).join(', ') || '',
  };
}

/**
 * Export user groups to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} groups — user group data
 * @param {string} [filename='user-groups.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportUserGroupsCsv(groups, filename = 'user-groups.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...USER_GROUP_HEADERS_ADMIN] : [];

  // Build data headers from visible columns in defined order
  const dataHeaders = USER_GROUP_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => USER_GROUP_HEADERS_MAP[colKey]);

  const headers = [...adminHeaders, ...dataHeaders];
  const rows = groups.map(transformUserGroup);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Devices Export
// ---------------------------------------------------------------------------

const DEVICE_HEADERS_ADMIN = [
  { label: 'Device ID', key: 'device_id' },
];

/** CSV header map for nurse call device export — keyed by table column key */
const DEVICE_HEADERS_MAP = {
  device_name: { label: 'Name', key: 'device_name' },
  descriptions: { label: 'Description', key: 'descriptions' },
  inovonics_model_name: { label: 'Device Model', key: 'inovonics_model_name' },
  manufacture_uuid: { label: 'Serial Number', key: 'manufacture_uuid' },
  residents: { label: 'Resident Name', key: '_resident_names' },
  area_name: { label: 'Area Name', key: 'area_name' },
  low_battery: { label: 'Battery', key: 'low_battery' },
  tamper: { label: 'Tamper', key: 'tamper' },
  offline: { label: 'Offline Status', key: 'offline' },
  last_alarm: { label: 'Last Alarm', key: 'last_alarm' },
  last_active: { label: 'Last Active', key: 'last_active' },
  is_active: { label: 'Active Status', key: 'is_active' },
};

/** Column order for nurse call device CSV output */
const DEVICE_COLUMN_ORDER = [
  'device_name', 'descriptions', 'inovonics_model_name', 'manufacture_uuid',
  'residents', 'area_name', 'low_battery', 'tamper', 'offline',
  'last_alarm', 'last_active', 'is_active',
];

/**
 * Transform a device row for CSV export — flatten residents array to names string.
 */
function transformDevice(d) {
  return {
    ...d,
    _resident_names: (d.residents || []).map((r) => r.resident_name).filter(Boolean).join(', ') || d.resident_name || '',
  };
}

/**
 * Export devices to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} devices — device data
 * @param {string} [filename='devices.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportDevicesCsv(devices, filename = 'devices.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...DEVICE_HEADERS_ADMIN] : [];
  const dataHeaders = DEVICE_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => DEVICE_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = devices.map(transformDevice);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wandering Devices Export
// ---------------------------------------------------------------------------

/** CSV header map for wandering device export — keyed by table column key */
const WANDERING_HEADERS_MAP = {
  device_name: { label: 'Name', key: 'device_name' },
  descriptions: { label: 'Description', key: 'descriptions' },
  model_name: { label: 'Model', key: 'model_name' },
  manufacture_uuid: { label: 'Badge Number', key: 'manufacture_uuid' },
  resident_name: { label: 'Resident', key: 'resident_name' },
  area_name: { label: 'Area', key: 'area_name' },
  is_active: { label: 'Active Status', key: '_active_status' },
};

/** Column order for wandering device CSV output */
const WANDERING_COLUMN_ORDER = [
  'device_name', 'descriptions', 'model_name', 'manufacture_uuid',
  'resident_name', 'area_name', 'is_active',
];

/**
 * Transform a wandering device row for CSV export.
 */
function transformWanderingDevice(d) {
  return {
    ...d,
    _active_status: d.is_active ? 'Active' : 'Inactive',
  };
}

/**
 * Export wandering devices to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} devices — wandering device data
 * @param {string} [filename='wandering-devices.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportWanderingDevicesCsv(devices, filename = 'wandering-devices.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...DEVICE_HEADERS_ADMIN] : [];
  const dataHeaders = WANDERING_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => WANDERING_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = devices.map(transformWanderingDevice);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// System Devices Export
// ---------------------------------------------------------------------------

/** CSV header map for system device export — keyed by table column key */
const SYSTEM_HEADERS_MAP = {
  device_name: { label: 'Name', key: 'device_name' },
  model_name: { label: 'Model', key: 'model_name' },
  manufacture_uuid: { label: 'Serial Number', key: 'manufacture_uuid' },
  area_name: { label: 'Area', key: 'area_name' },
  network_id: { label: 'Network', key: 'network_id' },
  last_active: { label: 'Last Active', key: 'last_active' },
  mac_addr: { label: 'Last Known Server', key: 'mac_addr' },
};

/** Column order for system device CSV output */
const SYSTEM_COLUMN_ORDER = [
  'device_name', 'model_name', 'manufacture_uuid', 'area_name',
  'network_id', 'last_active', 'mac_addr',
];

/**
 * Export system devices to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} devices — system device data
 * @param {string} [filename='system-devices.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportSystemDevicesCsv(devices, filename = 'system-devices.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...DEVICE_HEADERS_ADMIN] : [];
  const dataHeaders = SYSTEM_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => SYSTEM_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const csv = buildCsv(headers, devices);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Speaker Devices Export (SIP / Grandstream)
// ---------------------------------------------------------------------------

/** CSV header map for speaker device export — keyed by table column key */
const SPEAKER_HEADERS_MAP = {
  device_name: { label: 'Name', key: 'device_name' },
  descriptions: { label: 'Description', key: 'descriptions' },
  model_name: { label: 'Model', key: 'model_name' },
  manufacture_uuid: { label: 'Serial Number', key: 'manufacture_uuid' },
  residents: { label: 'Residents', key: '_resident_names' },
  area_name: { label: 'Area', key: 'area_name' },
  sip_extension: { label: 'Extension', key: 'sip_extension' },
  is_active: { label: 'Active Status', key: '_active_status' },
};

/** Column order for speaker device CSV output */
const SPEAKER_COLUMN_ORDER = [
  'device_name', 'descriptions', 'model_name', 'manufacture_uuid',
  'residents', 'area_name', 'sip_extension', 'is_active',
];

/**
 * Transform a speaker device row for CSV export — flatten residents array.
 */
function transformSpeakerDevice(d) {
  return {
    ...d,
    _resident_names: (d.residents || []).map((r) => r.resident_name).filter(Boolean).join(', '),
    _active_status: d.is_active ? 'Active' : 'Inactive',
  };
}

/**
 * Export speaker devices to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} devices — speaker device data
 * @param {string} [filename='speaker-devices.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportSpeakerDevicesCsv(devices, filename = 'speaker-devices.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...DEVICE_HEADERS_ADMIN] : [];
  const dataHeaders = SPEAKER_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => SPEAKER_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = devices.map(transformSpeakerDevice);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Servers Export
// ---------------------------------------------------------------------------

const SERVER_HEADERS_ADMIN = [
  { label: 'Device ID', key: 'palcare_device_id' },
];

/** CSV header map for server export — keyed by table column key */
const SERVER_HEADERS_MAP = {
  description: { label: 'Description', key: 'description' },
  palcare_interface_name: { label: 'Service', key: 'palcare_interface_name' },
  area_name: { label: 'Area', key: 'area_name' },
  ip_address: { label: 'IP Address', key: 'ip_address' },
  mac_addr: { label: 'Mac Address', key: 'mac_addr' },
  is_active: { label: 'Status', key: '_status' },
  last_heard: { label: 'Last Update', key: 'last_heard' },
};

/** Column order for server CSV output */
const SERVER_COLUMN_ORDER = [
  'description', 'palcare_interface_name', 'area_name', 'ip_address',
  'mac_addr', 'is_active', 'last_heard',
];

/**
 * Transform a server row for CSV export.
 */
function transformServer(s) {
  return {
    ...s,
    _status: s.is_active ? 'Active' : 'Backup',
  };
}

/**
 * Export servers to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} servers — server data
 * @param {string} [filename='servers.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportServersCsv(servers, filename = 'servers.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...SERVER_HEADERS_ADMIN] : [];
  const dataHeaders = SERVER_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => SERVER_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = servers.map(transformServer);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Reason Codes Export
// ---------------------------------------------------------------------------

/** CSV column headers for reason code export */
const REASON_CODE_HEADERS = [
  { label: 'Reason', key: 'reason_code_description' },
  { label: 'Reason Category', key: 'pal_reason_description' },
];

/** Extra column visible only to higher-authority users (weight > 80) */
const REASON_CODE_HEADERS_ADMIN = [
  { label: 'Reason Code ID', key: 'cloud_reason_code_id' },
];

/**
 * Export reason codes to CSV and trigger download.
 *
 * @param {Array<object>} reasons — reason code data (must have pal_reason_description resolved)
 * @param {string} [filename='reason-codes.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 */
export function exportReasonCodesCsv(reasons, filename = 'reason-codes.csv', myRoleWeight = 10) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...REASON_CODE_HEADERS_ADMIN] : [];
  const headers = [...adminHeaders, ...REASON_CODE_HEADERS];
  const csv = buildCsv(headers, reasons);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Preform Notes Export
// ---------------------------------------------------------------------------

/** CSV column headers for preform note template export */
const PREFORM_NOTE_HEADERS = [
  { label: 'Note Template', key: 'preform_note' },
  { label: 'Initially Added', key: '_create_time' },
  { label: 'Last Updated', key: '_last_modified' },
  { label: 'Last Updated By', key: 'display_name' },
];

/**
 * Export preform note templates to CSV and trigger download.
 * Expects rows with pre-formatted date fields (_create_time, _last_modified).
 *
 * @param {Array<object>} notes — preform note data
 * @param {string} [filename='note-templates.csv'] — download filename
 */
export function exportPreformNotesCsv(notes, filename = 'note-templates.csv') {
  const csv = buildCsv(PREFORM_NOTE_HEADERS, notes);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Audit Logs Export
// ---------------------------------------------------------------------------

/** CSV column headers for audit log export */
const AUDIT_LOG_HEADERS = [
  { label: 'Timestamp', key: '_create_time' },
  { label: 'Action', key: 'action' },
  { label: 'From', key: 'prev_value' },
  { label: 'To', key: 'new_value' },
  { label: 'Source', key: 'source' },
  { label: 'User', key: 'display_name' },
];

/**
 * Export audit logs to CSV and trigger download.
 * Expects rows with pre-formatted _create_time field.
 *
 * @param {Array<object>} logs — audit log data
 * @param {string} [filename='audit-logs.csv'] — download filename
 */
export function exportAuditLogsCsv(logs, filename = 'audit-logs.csv') {
  const csv = buildCsv(AUDIT_LOG_HEADERS, logs);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Staging Areas Export (Master File Upload Preview)
// ---------------------------------------------------------------------------

/** CSV column headers for staging area export */
const STAGING_AREA_HEADERS = [
  { label: 'Name', key: 'area_name' },
  { label: 'Area Type', key: 'area_type_description' },
  { label: 'Residents', key: 'resident_name' },
  { label: 'Devices', key: 'device_name' },
];

/**
 * Export staging areas to CSV and trigger download.
 *
 * @param {Array<object>} areas — staging area data
 * @param {string} [filename='staging-areas.csv'] — download filename
 */
export function exportStagingAreasCsv(areas, filename = 'staging-areas.csv') {
  const csv = buildCsv(STAGING_AREA_HEADERS, areas);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Staging Residents Export (Master File Upload Preview)
// ---------------------------------------------------------------------------

/** CSV column headers for staging resident export */
const STAGING_RESIDENT_HEADERS = [
  { label: 'First Name', key: 'resident_first_name' },
  { label: 'Last Name', key: 'resident_last_name' },
  { label: 'Area', key: 'area_name' },
  { label: 'Phone #', key: 'resident_phone' },
  { label: 'Email', key: 'resident_email' },
];

/**
 * Export staging residents to CSV and trigger download.
 *
 * @param {Array<object>} residents — staging resident data
 * @param {string} [filename='staging-residents.csv'] — download filename
 */
export function exportStagingResidentsCsv(residents, filename = 'staging-residents.csv') {
  const csv = buildCsv(STAGING_RESIDENT_HEADERS, residents);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Staging Devices Export (Master File Upload Preview)
// ---------------------------------------------------------------------------

/** CSV column headers for staging device export */
const STAGING_DEVICE_HEADERS = [
  { label: 'Name', key: 'device_name' },
  { label: 'Description', key: 'descriptions' },
  { label: 'Model', key: 'model_name' },
  { label: 'Serial Number', key: 'manufacture_uuid' },
  { label: 'Resident', key: 'resident_name' },
  { label: 'Area', key: 'area_name' },
];

/**
 * Export staging devices to CSV and trigger download.
 *
 * @param {Array<object>} devices — staging device data
 * @param {string} [filename='staging-devices.csv'] — download filename
 */
export function exportStagingDevicesCsv(devices, filename = 'staging-devices.csv') {
  const csv = buildCsv(STAGING_DEVICE_HEADERS, devices);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// All Servers Export (cross-campus)
// ---------------------------------------------------------------------------

const ALL_SERVERS_HEADERS = [
  { label: 'Campus ID', key: 'campus_id' },
  { label: 'Campus Name', key: 'campus_name' },
  { label: 'Corporation ID', key: 'corporate_id' },
  { label: 'Corporation Name', key: 'corporate_name' },
  { label: 'Code Version', key: 'version_code' },
  { label: 'Checkin Time', key: 'checkin_time' },
  { label: 'Recent Alarm Time', key: 'recent_alarm_input_time' },
  { label: 'DB Version', key: 'version_database' },
];

/**
 * Export all servers (cross-campus) to CSV and trigger download.
 *
 * @param {Array<object>} servers — all-servers data
 * @param {string} [filename='all_servers.csv'] — download filename
 */
export function exportAllServersCsv(servers, filename = 'all_servers.csv') {
  const csv = buildCsv(ALL_SERVERS_HEADERS, servers);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Campuses Export (cross-campus)
// ---------------------------------------------------------------------------

const CAMPUS_HEADERS = [
  { label: 'Campus ID', key: 'campus_id' },
  { label: 'Campus 2.0 ID', key: 'campus_guid' },
  { label: 'Campus Name', key: 'campus_name' },
  { label: 'Corporation Name', key: 'corporate_name' },
  { label: 'Address', key: 'campus_street_address' },
  { label: 'City', key: 'campus_city' },
  { label: 'State', key: 'campus_state' },
  { label: 'Zip Code', key: 'campus_zip' },
  { label: 'Email', key: 'campus_email' },
  { label: 'Phone #', key: 'campus_phone' },
  { label: 'Has Checkin', key: '_has_checkin' },
  { label: 'Has Rounds', key: '_has_rounds' },
  { label: 'Has Light Ctrl', key: '_has_lightctrl' },
  { label: 'Has Offsite', key: '_has_offsite' },
  { label: 'Service Plan', key: '_service_plan' },
];

/**
 * Transform a campus row for CSV export — convert booleans to readable labels.
 *
 * Reads the same fields, with the same truthiness, as the Campuses table's checkmark /
 * dash columns (CampusesTable renderBoolIcon), so the file always matches the screen. It
 * used to read use_checkin / use_rounds / use_lightctrl / use_offsite, which aren't the
 * table's fields, so the export didn't follow what the table showed.
 */
function transformCampus(c) {
  return {
    ...c,
    _has_checkin: c.is_resident_checkin ? 'Yes' : 'No',
    _has_rounds: c.is_rounding ? 'Yes' : 'No',
    _has_lightctrl: c.udp_broadcast ? 'Yes' : 'No',
    _has_offsite: c.offsite_active ? 'Yes' : 'No',
    _service_plan: c.service_plan_active ? 'Plan Active' : 'No Plan',
  };
}

/**
 * Export campuses (cross-campus) to CSV and trigger download.
 *
 * @param {Array<object>} campuses — campus data
 * @param {string} [filename='campuses.csv'] — download filename
 */
export function exportCampusesCsv(campuses, filename = 'campuses.csv') {
  const rows = campuses.map(transformCampus);
  const csv = buildCsv(CAMPUS_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Corporations Export (cross-corporation)
// ---------------------------------------------------------------------------

const CORPORATION_HEADERS = [
  { label: 'Corporate Name', key: 'corporate_name' },
  { label: 'Corporate ID', key: 'corporate_id' },
  { label: 'Corporate 2.0 ID', key: 'corporate_guid' },
];

/**
 * Export corporations to CSV and trigger download.
 *
 * @param {Array<object>} corporations — corporation data
 * @param {string} [filename='corporations.csv'] — download filename
 */
export function exportCorporationsCsv(corporations, filename = 'corporations.csv') {
  const csv = buildCsv(CORPORATION_HEADERS, corporations);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wellness Settings — Check-in Windows
// ---------------------------------------------------------------------------

const CHECKIN_WINDOW_HEADERS = [
  { label: 'Description', key: 'description' },
  { label: 'Start Time', key: 'window_start_display' },
  { label: 'End Time', key: 'window_end_display' },
  { label: 'Alarm On', key: 'generate_alarm_display' },
  { label: 'Alarm Time', key: 'alarm_at_display' },
  { label: 'Enabled', key: 'enabled_display' },
  { label: 'Resident Groups', key: 'resident_group_count' },
];

/** Extra column visible only to higher-authority users (weight > 80) */
const WINDOW_HEADERS_ADMIN = [
  { label: 'Window ID', key: 'window_id' },
];

export function exportCheckinWindowsCsv(rows, filename = 'checkin-windows.csv', myRoleWeight = 10) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...WINDOW_HEADERS_ADMIN] : [];
  const headers = [...adminHeaders, ...CHECKIN_WINDOW_HEADERS];
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wellness Settings — Rounds Windows
// ---------------------------------------------------------------------------

const ROUNDS_WINDOW_HEADERS = [
  { label: 'Description', key: 'description' },
  { label: 'Start Time', key: 'window_start_display' },
  { label: 'End Time', key: 'window_end_display' },
  { label: '# Checks', key: 'interval' },
  { label: 'Alarm On', key: 'generate_alarm_display' },
  { label: 'Alarm Time', key: 'alarm_at_display' },
  { label: 'Enabled', key: 'enabled_display' },
  { label: 'Area Groups', key: 'area_group_count' },
];

export function exportRoundsWindowsCsv(rows, filename = 'rounds-windows.csv', myRoleWeight = 10) {
  const adminHeaders = iAmHigherAuthority(80, myRoleWeight) ? [...WINDOW_HEADERS_ADMIN] : [];
  const headers = [...adminHeaders, ...ROUNDS_WINDOW_HEADERS];
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wellness Settings — Careflow
// ---------------------------------------------------------------------------

const CAREFLOW_HEADERS = [
  { label: 'Description', key: 'careflow_description' },
  { label: 'Category', key: 'event_category_display' },
  { label: 'Status', key: 'status_display' },
  { label: 'Active Time', key: 'time_display' },
  { label: 'Mobile', key: 'mobile_display' },
  { label: 'Text', key: 'text_display' },
  { label: 'Voice', key: 'voice_display' },
  { label: 'Email', key: 'email_display' },
  { label: 'Pager', key: 'pager_display' },
  { label: 'Offsite', key: 'offsite_display' },
  { label: 'Mute', key: 'mute_display' },
  { label: 'Reporting', key: 'reporting_display' },
  { label: 'References', key: 'references_count' },
];

/** Map of careflow output column keys → campusSettings flag keys. Mirrors
 *  OUTPUT_COLUMN_FLAG_KEYS in CareflowTable.jsx. */
const CAREFLOW_OUTPUT_FLAG_KEYS = {
  mobile_display: 'app_active',
  text_display: 'text_messaging_active',
  voice_display: 'voice_active',
  email_display: 'email_active',
  pager_display: 'pager_active',
  offsite_display: 'offsite_active',
};

/**
 * Export careflows to CSV. When `campusSettings` is provided, output columns
 * (Mobile/Text/Voice/Email/Pager/Offsite) whose global toggle is off are
 * dropped from the CSV.
 *
 * @param {Array<object>} rows — careflow rows (already enriched)
 * @param {string} [filename='careflows.csv']
 * @param {object} [campusSettings] — global output settings flags
 */
export function exportCareflowCsv(rows, filename = 'careflows.csv', campusSettings) {
  const headers = campusSettings
    ? CAREFLOW_HEADERS.filter((h) => {
        const flagKey = CAREFLOW_OUTPUT_FLAG_KEYS[h.key];
        return !flagKey || campusSettings[flagKey] === 1;
      })
    : CAREFLOW_HEADERS;
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Environmental Settings — Temperature / Humidity / Battery
// ---------------------------------------------------------------------------

const ENV_TEMP_HUMIDITY_HEADERS = [
  { label: 'Description', key: 'description' },
  { label: 'Type', key: 'type_display' },
  { label: 'Low Threshold', key: 'low_alarm' },
  { label: 'High Threshold', key: 'high_alarm' },
  { label: 'Alarm On', key: 'alarm_display' },
  { label: 'Report Interval (min)', key: 'interval_display' },
  { label: 'Device Count', key: 'device_count' },
];

const ENV_BATTERY_HEADERS = [
  { label: 'Description', key: 'description' },
  { label: 'Type', key: 'type_display' },
  { label: 'Low Battery (V)', key: 'low_alarm' },
  { label: 'Alarm On', key: 'alarm_display' },
  { label: 'Report Interval (min)', key: 'interval_display' },
  { label: 'Device Count', key: 'device_count' },
];

// ---------------------------------------------------------------------------
// Light Controller Settings — Channels
// ---------------------------------------------------------------------------

const LIGHT_CHANNEL_HEADERS = [
  { label: 'Channel #', key: 'channel_id' },
  { label: 'Name', key: 'channel_name' },
  { label: 'Color', key: 'color_label' },
  { label: 'Flashing', key: 'flashing_display' },
  { label: 'Devices', key: 'device_count' },
];

/**
 * Export light controller channels to CSV and trigger download.
 *
 * @param {Array<object>} rows — enriched channel data (with color_label, flashing_display, device_count)
 * @param {string} [controllerName='channels'] — controller name for default filename
 * @param {string} [filename] — override download filename
 */
export function exportLightChannelsCsv(rows, controllerName = 'channels', filename) {
  const defaultFilename = `${controllerName.replace(/\s+/g, '-').toLowerCase()}-channels.csv`;
  const csv = buildCsv(LIGHT_CHANNEL_HEADERS, rows);
  downloadCsv(csv, filename || defaultFilename);
}

export function exportEnvironmentalCsv(rows, envType, filename) {
  const isBattery = envType === 'battery';
  const headers = isBattery ? ENV_BATTERY_HEADERS : ENV_TEMP_HUMIDITY_HEADERS;
  const defaultFilename = `${envType || 'environmental'}-settings.csv`;
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename || defaultFilename);
}

// ---------------------------------------------------------------------------
// PalPendant Display Profiles Export
// ---------------------------------------------------------------------------

const PENDANT_PROFILE_HEADERS = [
  { label: 'Profile Name', key: 'profile_name' },
];

/**
 * Export pendant display profiles to CSV and trigger download.
 *
 * @param {Array<object>} rows — profile data
 * @param {string} [filename='pendant-profiles.csv'] — download filename
 */
export function exportPendantProfilesCsv(rows, filename = 'pendant-profiles.csv') {
  const csv = buildCsv(PENDANT_PROFILE_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Alert Details Report Export
// ---------------------------------------------------------------------------

/** Base CSV column headers for alert details export (matches V1 layout) */
const ALERT_DETAIL_HEADERS = [
  { label: 'Event Start', key: 'event_datetime_local' },
  { label: 'Event Category', key: 'event_category_name' },
  { label: 'Event Type', key: 'event_type_name' },
  { label: 'Resident', key: 'resident_name' },
  { label: 'Area', key: 'area_name' },
  { label: 'Located Area', key: 'estimated_location_name' },
  { label: 'Device Name', key: 'description' },
  { label: 'Device Number', key: 'manufacture_uuid' },
  { label: 'Accepted By', key: 'display_name' },
  { label: 'Event Accepted', key: 'accept_datetime_local' },
  { label: 'Event Cleared', key: 'ack_datetime_local' },
  { label: 'Event Finished', key: 'finish_datetime_local' },
  { label: 'Response (min)', key: 'response_time' },
  { label: 'Time to Connect (min)', key: 'time_to_connect' },
  { label: 'Intercom Used', key: 'intercom_used' },
  { label: 'Intercom Answered', key: 'intercom_answered' },
  { label: 'Service Time (min)', key: 'service_time' },
  { label: 'Reason', key: 'reason_code_description' },
];

/**
 * Map table column keys → CSV header keys for Alert Details.
 * Some table columns combine multiple data fields (e.g. "Category/Type" → two CSV columns).
 */
const ALERT_TABLE_TO_CSV_MAP = {
  event_datetime_local: ['event_datetime_local'],
  event_type_name: ['event_category_name', 'event_type_name'],
  resident_name: ['resident_name'],
  area_name: ['area_name'],
  estimated_location_name: ['estimated_location_name'],
  description: ['description', 'manufacture_uuid'],
  display_name: ['display_name'],
  ack_datetime_local: ['accept_datetime_local', 'ack_datetime_local'],
  finish_datetime_local: ['finish_datetime_local'],
  response_time: ['response_time'],
  time_to_connect: ['time_to_connect', 'intercom_used', 'intercom_answered'],
  service_time: ['service_time'],
  reason_code_description: ['reason_code_description'],
};

/* Time to Connect columns, Grandstream only (docs/TIME_TO_CONNECT_PLAN.md). Kept
   when the rows carry the key, not by trusting visibleColumns: a column preference
   can outlive a switch to a non-Grandstream campus, and Hourly Summary shares these
   exports without computing the KPI. A row set without it gets no empty column. */
const TIME_TO_CONNECT_KEYS = ['time_to_connect', 'intercom_used', 'intercom_answered'];

function withoutAbsentConnectColumns(headers, rows) {
  if ((rows || []).some((row) => row && 'time_to_connect' in row)) return headers;
  return headers.filter((h) => !TIME_TO_CONNECT_KEYS.includes(h.key));
}

/** Filter alert detail headers by visible table columns */
function filterAlertHeaders(headers, visibleColumns) {
  if (!visibleColumns || visibleColumns.length === 0) return headers;
  const allowedKeys = new Set();
  visibleColumns.forEach((tableKey) => {
    const csvKeys = ALERT_TABLE_TO_CSV_MAP[tableKey];
    if (csvKeys) csvKeys.forEach((k) => allowedKeys.add(k));
  });
  return headers.filter((h) => allowedKeys.has(h.key));
}

/**
 * Export alert details to CSV with dynamic note columns.
 *
 * @param {Array<object>} rows — event data with optional `notes` arrays
 * @param {number} maxNotes — maximum note count across all rows
 * @param {string} [filename='alert-details.csv']
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters CSV columns)
 */
export function exportAlertDetailsCsv(rows, maxNotes = 0, filename = 'alert-details.csv', visibleColumns) {
  // Build dynamic note headers
  const noteHeaders = [];
  for (let i = 1; i <= maxNotes; i++) {
    noteHeaders.push({ label: `Note ${i}`, key: `_note_${i}` });
  }
  const filteredBase = withoutAbsentConnectColumns(filterAlertHeaders(ALERT_DETAIL_HEADERS, visibleColumns), rows);
  const headers = [...filteredBase, ...noteHeaders];

  // Flatten notes into row fields
  const transformed = rows.map((row) => {
    const flat = { ...row };
    const notes = row.notes || [];
    for (let i = 0; i < maxNotes; i++) {
      flat[`_note_${i + 1}`] = notes[i] ? notes[i].note || '' : '';
    }
    /* Time to Connect stays BLANK for an alert without an answered intercom call: a
       CSV is spreadsheet data, and "N/A" / "No answer" in a minutes column breaks every
       formula. Intercom Used and Intercom Answered say why it is blank. */
    flat.intercom_answered = intercomAnsweredLabel(row);
    flat.intercom_used = intercomUsedLabel(row.intercom_used);
    return flat;
  });

  const csv = buildCsv(headers, transformed);
  downloadCsv(csv, filename);
}

/**
 * Full PDF column definitions for Alert Details (label + key pairs for filtering).
 */
const ALERT_PDF_COLUMNS = [
  { label: 'Event Start', key: 'event_datetime_local' },
  { label: 'Category', key: 'event_category_name' },
  { label: 'Type', key: 'event_type_name' },
  { label: 'Resident', key: 'resident_name' },
  { label: 'Area', key: 'area_name' },
  { label: 'Located Area', key: 'estimated_location_name' },
  { label: 'Device', key: 'description' },
  { label: 'Accepted By', key: 'display_name' },
  { label: 'Cleared', key: 'ack_datetime_local' },
  { label: 'Finished', key: 'finish_datetime_local' },
  { label: 'Response', key: 'response_time' },
  { label: 'Connect', key: 'time_to_connect' },
  { label: 'Service', key: 'service_time' },
  { label: 'Reason', key: 'reason_code_description' },
];

/**
 * Export alert details to PDF (landscape A4).
 *
 * @param {Array<object>} rows — event data
 * @param {string} title — report title
 * @param {string} dateRange — e.g. "01/15/2026 12:00 AM - 01/16/2026 11:59 PM"
 * @param {string} [filename='alert-details.pdf']
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters PDF columns)
 */
export function exportAlertDetailsPdf(rows, title = 'Alert Details Report', dateRange = '', filename = 'alert-details.pdf', visibleColumns) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const filteredCols = withoutAbsentConnectColumns(filterAlertHeaders(ALERT_PDF_COLUMNS, visibleColumns), rows);
  const pdfHeaders = filteredCols.map((col) => col.label);

  const formatValue = (row, key) => {
    if (key === 'service_time') {
      /* NULL is "not measured", 0 is a real zero -- the proc distinguishes them
         now, so a genuine 0.0 prints as 0.0 rather than a dash (E2). */
      return row[key] != null && row[key] !== '' ? String(row[key]) : '-';
    }
    if (key === 'response_time') {
      return row[key] != null ? String(row[key]) : '';
    }
    if (key === 'time_to_connect') {
      // Same text as the table cell: minutes, "No answer" or N/A -- never a dash or 0.
      return timeToConnectCell(row).text;
    }
    return row[key] != null ? String(row[key]) : '';
  };

  /* Notes follow the CSV rule: added whenever any row has one, whatever the column
     toggles say. One cell per event rather than a Note N column per note -- the page is
     already full -- numbered once there is more than one so they match the CSV's order. */
  const hasNotes = rows.some((row) => (row.notes || []).length > 0);
  if (hasNotes) pdfHeaders.push('Notes');
  const notesCell = (row) => {
    const notes = (row.notes || []).map((n) => n.note || '').filter(Boolean);
    return notes.length > 1 ? notes.map((n, i) => `${i + 1}. ${n}`).join('\n') : (notes[0] || '');
  };

  const pdfRows = rows.map((row) => {
    const cells = filteredCols.map((col) => formatValue(row, col.key));
    if (hasNotes) cells.push(notesCell(row));
    return cells;
  });

  // Title
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    // A wider Notes column so free text wraps there instead of squeezing every other column.
    columnStyles: hasNotes ? { [pdfHeaders.length - 1]: { cellWidth: 45 } } : {},
    margin: { left: 10, right: 10 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Call Logs Report Export
// ---------------------------------------------------------------------------

// CSV/PDF columns (label + key). call_start_time is UTC, so it's localized into
// `call_start_local` by the row transform below before export.
const CALL_LOGS_COLUMNS = [
  { label: 'Call Start', key: 'call_start_local' },
  { label: 'Duration', key: 'call_length' },
  { label: 'Outcome', key: 'call_outcome_label' },
  { label: 'End Reason', key: 'call_end_reason_label' },
  { label: 'Talk Time', key: 'talk_time' },
  { label: 'Call Type', key: 'call_type_name' },
  { label: 'Event', key: 'event_id' },
  { label: 'Caller', key: 'caller' },
  { label: 'Receiver', key: 'receiver' },
];

// Maps a table column key (visibleColumns) → its export column key(s).
const CALL_LOGS_TABLE_TO_EXPORT = {
  call_start_time: ['call_start_local'],
  call_length: ['call_length'],
  call_outcome: ['call_outcome_label', 'call_end_reason_label'],
  talk_length: ['talk_time'],
  call_type_name: ['call_type_name'],
  event_id: ['event_id'],
  caller: ['caller'],
  receiver: ['receiver'],
};

function filterCallLogsColumns(visibleColumns) {
  if (!visibleColumns || visibleColumns.length === 0) return CALL_LOGS_COLUMNS;
  const allowed = new Set();
  visibleColumns.forEach((k) => (CALL_LOGS_TABLE_TO_EXPORT[k] || []).forEach((c) => allowed.add(c)));
  return CALL_LOGS_COLUMNS.filter((c) => allowed.has(c.key));
}

/** Add a campus-local call start (call_start_time is UTC) and the outcome / talk time as
    the table shows them (utils/callOutcome.js) to each row for export. Calls logged before
    outcomes were recorded export those blank. */
function callLogsExportRows(rows, tzOffset) {
  return (rows || []).map((r) => ({
    ...r,
    call_start_local: convertIsoUTCToLocal(r.call_start_time, tzOffset) || '',
    call_outcome_label: callOutcomeLabel(r),
    call_end_reason_label: callEndReasonLabel(r),
    talk_time: callTalkLength(r) || '',
  }));
}

/**
 * Export call logs to CSV.
 *
 * @param {Array<object>} rows — call records
 * @param {string} [filename='call-logs.csv']
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters export columns)
 * @param {number} [tzOffset] — campus tz_offset for localizing call start
 */
export function exportCallLogsCsv(rows, filename = 'call-logs.csv', visibleColumns, tzOffset) {
  const headers = filterCallLogsColumns(visibleColumns);
  const csv = buildCsv(headers, callLogsExportRows(rows, tzOffset));
  downloadCsv(csv, filename);
}

/**
 * Export call logs to PDF (landscape A4).
 *
 * @param {Array<object>} rows — call records
 * @param {string} [title='Call Logs Report']
 * @param {string} [dateRange=''] — e.g. "01/15/2026 12:00 AM – 01/16/2026 11:59 PM"
 * @param {string} [filename='call-logs.pdf']
 * @param {Array<string>} [visibleColumns]
 * @param {number} [tzOffset]
 */
export function exportCallLogsPdf(rows, title = 'Call Logs Report', dateRange = '', filename = 'call-logs.pdf', visibleColumns, tzOffset) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const cols = filterCallLogsColumns(visibleColumns);
  const exportRows = callLogsExportRows(rows, tzOffset);
  const pdfHeaders = cols.map((c) => c.label);
  const pdfRows = exportRows.map((row) => cols.map((c) => (row[c.key] != null ? String(row[c.key]) : '')));

  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 10, right: 10 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Hourly Summary Report Export
// ---------------------------------------------------------------------------

/** CSV column headers for hourly summary export */
const HOURLY_SUMMARY_HEADERS = [
  { label: 'Hour of Day', key: 'display_hour' },
  { label: '# Events', key: 'count' },
  { label: 'Avg Response (min)', key: 'avg_response_time' },
  { label: 'Min Response (min)', key: 'min_response_time' },
  { label: 'Max Response (min)', key: 'max_response_time' },
  { label: 'Avg Service Time (min)', key: 'avg_service_time' },
];

/* Per-hour Time to Connect (docs/TIME_TO_CONNECT_PLAN.md, phase 2), Grandstream only.
   Only when the hours carry it, so a failed connect lookup adds no empty columns. */
const HOURLY_CONNECT_HEADERS = [
  { label: 'Avg Time to Connect (min)', key: 'avg_time_to_connect' },
  { label: 'Intercom Usage', key: 'intercom_usage_pct' },
];

const hoursCarryConnect = (rows) => (rows || []).some((row) => row && 'avg_time_to_connect' in row);

/**
 * Export hourly summary breakdown to CSV.
 *
 * @param {Array<object>} rows — 24 hourly aggregate rows
 * @param {string} [filename='hourly-summary.csv']
 * @param {boolean} [isGrandstream] — adds the Time to Connect columns (when the rows carry them)
 */
export function exportHourlySummaryCsv(rows, filename = 'hourly-summary.csv', isGrandstream = false) {
  const withConnect = isGrandstream && hoursCarryConnect(rows);
  /* Like the detail CSV, an hour with alerts but no intercom leaves the minutes
     BLANK (spreadsheet data), and its usage reads a real 0.0%. */
  const headers = withConnect ? [...HOURLY_SUMMARY_HEADERS, ...HOURLY_CONNECT_HEADERS] : HOURLY_SUMMARY_HEADERS;
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

/**
 * Export hourly summary breakdown to PDF (landscape A4).
 *
 * @param {Array<object>} rows — 24 hourly aggregate rows
 * @param {string} title — report title
 * @param {string} dateRange — e.g. "Jan 15, 2026 12:00 AM - Jan 16, 2026 11:59 PM"
 * @param {string} [filename='hourly-summary.pdf']
 * @param {boolean} [isGrandstream] — adds the Time to Connect columns (when the rows carry them)
 */
export function exportHourlySummaryPdf(rows, title = 'Hourly Summary Report', dateRange = '', filename = 'hourly-summary.pdf',
                                       isGrandstream = false) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const withConnect = isGrandstream && hoursCarryConnect(rows);

  const pdfHeaders = [
    'Hour of Day', '# Events', 'Avg Response (min)',
    'Min Response (min)', 'Max Response (min)', 'Avg Service Time (min)',
    ...(withConnect ? ['Avg Time to Connect (min)', 'Intercom Usage'] : []),
  ];

  // `== null` alone -- see AlertSummaryView.formatServiceTime. sp_getHourlySummary
  // now returns NULL for an unmeasurable hour, so a zero arm would print a real
  // sub-three-second service time as though it had never been measured.
  const formatService = (val) => {
    if (val == null || val === '') return '-';
    return String(val);
  };

  const pdfRows = rows.map((row) => [
    row.display_hour || '',
    row.count != null ? String(row.count) : '0',
    row.avg_response_time != null ? String(row.avg_response_time) : '',
    row.min_response_time != null ? String(row.min_response_time) : '',
    row.max_response_time != null ? String(row.max_response_time) : '',
    formatService(row.avg_service_time),
    /* No alerts that hour: '-'. Alerts but no intercom: N/A and a real 0.0%. */
    ...(withConnect ? [
      Number(row.count) > 0 ? (row.avg_time_to_connect ?? TIME_TO_CONNECT_NA) : '-',
      Number(row.count) > 0 ? (row.intercom_usage_pct ?? '-') : '-',
    ] : []),
  ]);

  // Title
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 14, right: 14 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Maintenance Report — CSV + PDF (on-premise and cloud)
// ---------------------------------------------------------------------------

const MAINTENANCE_ONPREMISE_CSV_HEADERS = [
  { label: 'Event Start', key: 'EventDateTime' },
  { label: 'Device', key: 'DeviceUse' },
  { label: 'Serial #', key: 'SerialNumber' },
  { label: 'Alert Type', key: 'AlertSubType' },
  { label: 'Resident Name', key: '_Resident_Names' },
  { label: 'Area', key: 'LocationArea' },
  { label: 'Location', key: 'LocationName' },
];

const MAINTENANCE_CLOUD_CSV_HEADERS = [
  { label: 'Detected (UTC)', key: 'event_datetime_utc' },
  { label: 'Condition', key: 'condition' },
  { label: 'Device Name', key: 'device_name' },
  { label: 'Device Serial #', key: 'manufacture_uuid' },
  { label: 'Area', key: 'area_name' },
  { label: 'Resident', key: 'resident_name' },
];

/**
 * Map table column keys → CSV header keys for Maintenance.
 * Some table columns are virtual/combined (e.g. _device_serial → DeviceUse + SerialNumber).
 */
const MAINTENANCE_ONPREMISE_TABLE_TO_CSV_MAP = {
  EventDateTime: ['EventDateTime'],
  _duration: [],
  AlertSubType: ['AlertSubType'],
  _residents: ['_Resident_Names'],
  _device_serial: ['DeviceUse', 'SerialNumber'],
  _location: ['LocationArea', 'LocationName'],
};

const MAINTENANCE_CLOUD_TABLE_TO_CSV_MAP = {
  event_datetime_utc: ['event_datetime_utc'],
  _duration: [],
  condition: ['condition'],
  _device_serial: ['device_name', 'manufacture_uuid'],
  area_name: ['area_name'],
  resident_name: ['resident_name'],
};

/** Filter maintenance headers by visible table columns */
function filterMaintenanceHeaders(headers, visibleColumns, tableMap) {
  if (!visibleColumns || visibleColumns.length === 0) return headers;
  const allowedKeys = new Set();
  visibleColumns.forEach((tableKey) => {
    const csvKeys = tableMap[tableKey];
    if (csvKeys) csvKeys.forEach((k) => allowedKeys.add(k));
  });
  return headers.filter((h) => allowedKeys.has(h.key));
}

/**
 * Flatten the Residents JSON string into comma-separated names.
 * V1 shape: `[{"fName":"John","lName":"Doe","id":5}]`
 */
function flattenResidents(row) {
  try {
    const arr = typeof row.Residents === 'string' ? JSON.parse(row.Residents) : row.Residents;
    if (Array.isArray(arr)) {
      return arr.map((r) => `${r.fName || ''} ${r.lName || ''}`.trim()).join(', ');
    }
    return '';
  } catch {
    return '';
  }
}

/**
 * Export maintenance report data to CSV.
 *
 * @param {Array} rows — report records
 * @param {boolean} isCloud — cloud vs on-premise column set
 * @param {string} filename
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters CSV columns)
 */
export function exportMaintenanceCsv(rows, isCloud, filename = 'maintenance-report.csv', visibleColumns) {
  const allHeaders = isCloud ? MAINTENANCE_CLOUD_CSV_HEADERS : MAINTENANCE_ONPREMISE_CSV_HEADERS;
  const tableMap = isCloud ? MAINTENANCE_CLOUD_TABLE_TO_CSV_MAP : MAINTENANCE_ONPREMISE_TABLE_TO_CSV_MAP;
  const headers = filterMaintenanceHeaders(allHeaders, visibleColumns, tableMap);

  // For on-premise, flatten the Residents JSON into a temporary key
  const mappedRows = isCloud
    ? rows
    : rows.map((row) => ({ ...row, _Resident_Names: flattenResidents(row) }));

  const csv = buildCsv(headers, mappedRows);
  downloadCsv(csv, filename);
}

/**
 * Export maintenance report data to PDF.
 *
 * @param {Array} rows — report records
 * @param {boolean} isCloud — cloud vs on-premise column set
 * @param {string} title — report title
 * @param {string} dateRange — date range display string
 * @param {string} filename
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters PDF columns)
 */
export function exportMaintenancePdf(rows, isCloud, title, dateRange, filename = 'maintenance-report.pdf', visibleColumns) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const allPdfCols = isCloud
    ? [
        { label: 'Detected (UTC)', key: 'event_datetime_utc' },
        { label: 'Condition', key: 'condition' },
        { label: 'Device Name', key: 'device_name' },
        { label: 'Device Serial #', key: 'manufacture_uuid' },
        { label: 'Area', key: 'area_name' },
        { label: 'Resident', key: 'resident_name' },
      ]
    : [
        { label: 'Event Start', key: 'EventDateTime' },
        { label: 'Device', key: 'DeviceUse' },
        { label: 'Serial #', key: 'SerialNumber' },
        { label: 'Alert Type', key: 'AlertSubType' },
        { label: 'Resident Name', key: '_Resident_Names' },
        { label: 'Area', key: 'LocationArea' },
        { label: 'Location', key: 'LocationName' },
      ];

  const tableMap = isCloud ? MAINTENANCE_CLOUD_TABLE_TO_CSV_MAP : MAINTENANCE_ONPREMISE_TABLE_TO_CSV_MAP;
  const filteredCols = filterMaintenanceHeaders(allPdfCols, visibleColumns, tableMap);
  const pdfHeaders = filteredCols.map((col) => col.label);

  // Prepare row data, flattening residents for on-premise
  const mappedRows = isCloud
    ? rows
    : rows.map((row) => ({ ...row, _Resident_Names: flattenResidents(row) }));

  const pdfRows = mappedRows.map((row) =>
    filteredCols.map((col) => row[col.key] != null ? String(row[col.key]) : '')
  );

  // Title
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 14, right: 14 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// EHR Tasks Report Export
// ---------------------------------------------------------------------------

/* One CSV for every bucket, with a Task Type column. No Care Level or staff Phone: those
   never leave the portal (docs/EHR_TASKS_REPORT_PLAN.md decision 6b). The same columns as
   the scheduled CSV (report_generator.py, report type 17). */
const EHR_TASKS_CSV_COLUMNS = [
  { label: 'Task Type', key: 'task_label', format: 'text' },
  { label: 'Name', key: 'name', format: 'text' },
  { label: 'Nickname', key: 'nickname', format: 'text' },
  { label: 'Area', key: 'area_name', format: 'text' },
  { label: 'Previous Area', key: 'old_area_name', format: 'text' },
  { label: 'Move-in Date', key: 'move_in_date', format: 'date' },
  { label: 'Move-out Date', key: 'move_out_date', format: 'date' },
  { label: 'Title', key: 'title', format: 'text' },
  { label: 'Email', key: 'email', format: 'text' },
  { label: 'Username', key: 'username', format: 'text' },
  { label: 'Received', key: 'received_utc', format: 'utc' },
  { label: 'Days Waiting', key: 'waiting_days', format: 'number' },
];

/**
 * Export the EHR Tasks report's rows (all buckets, in bucket order) to one CSV.
 *
 * @param {Array<object>} rows — report records (mysql_report ehrTasksReport)
 * @param {number} tzOffset — campus offset, for Received
 * @param {string} [filename]
 */
export function exportEhrTasksReportCsv(rows, tzOffset, filename = 'ehr-tasks-report.csv') {
  const flat = (rows || []).map((r) => {
    const out = {};
    EHR_TASKS_CSV_COLUMNS.forEach((c) => {
      out[c.key] = c.format === 'number' ? (Number(r[c.key]) || 0) : formatEhrCell(r, c, tzOffset);
    });
    return out;
  });
  downloadCsv(buildCsv(EHR_TASKS_CSV_COLUMNS, flat), filename);
}

/**
 * Export the EHR Tasks report to PDF: one table per bucket, each with its own columns,
 * minus the page-only ones.
 *
 * @param {Array<object>} rows — report records
 * @param {string[]} bucketKeys — the buckets shown, in order (empty buckets get a line)
 * @param {number} tzOffset
 * @param {string} title
 * @param {string} subtitle — "As of ..."
 * @param {string} [filename]
 */
export function exportEhrTasksReportPdf(rows, bucketKeys, tzOffset, title, subtitle = '', filename = 'ehr-tasks-report.pdf') {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  doc.setFontSize(14);
  doc.text(title || 'EHR Tasks', 14, 15);
  if (subtitle) {
    doc.setFontSize(9);
    doc.text(subtitle, 14, 22);
  }
  let y = subtitle ? 30 : 24;
  (bucketKeys || []).forEach((key) => {
    const bucket = ehrBucket(key);
    if (!bucket) return;
    const bucketRows = (rows || []).filter((r) => r.task_type === key);
    const cols = exportColumns(bucket);
    if (y > 180) { doc.addPage(); y = 15; }
    doc.setFontSize(11);
    doc.text(`${bucket.title} (${bucketRows.length})`, 14, y);
    if (bucketRows.length === 0) {
      doc.setFontSize(9);
      doc.text(`No ${bucket.noun} waiting for review.`, 14, y + 6);
      y += 14;
      return;
    }
    autoTable(doc, {
      head: [cols.map((c) => c.label)],
      body: bucketRows.map((r) => cols.map((c) => formatEhrCell(r, c, tzOffset))),
      startY: y + 3,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 10;
  });
  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Custom Event Text Templates Export
// ---------------------------------------------------------------------------

/** Column definitions for template CSV export */
const TEMPLATE_COLUMN_ORDER = ['output_type_name', 'text_format', 'field_count'];

const TEMPLATE_HEADERS_MAP = {
  output_type_name: 'Output Type',
  text_format: 'Template Format',
  field_count: 'Fields',
};

/**
 * Map visible column keys to their CSV-friendly equivalents.
 * The table uses 'text_format_display' but the actual data key is 'text_format'.
 */
const TEMPLATE_KEY_REMAP = {
  text_format_display: 'text_format',
};

/**
 * Export custom event text templates to CSV with only visible columns.
 *
 * @param {Array<object>} templates — enriched template data
 * @param {string} [filename='custom-event-text-templates.csv'] — download filename
 * @param {Array<string>} [visibleColumns] — visible column keys (exports all if omitted)
 */
export function exportTemplatesCsv(templates, filename = 'custom-event-text-templates.csv', visibleColumns) {
  // Remap visible column keys to data keys
  const mappedVisible = visibleColumns
    ? visibleColumns.map((k) => TEMPLATE_KEY_REMAP[k] || k)
    : null;

  const headers = TEMPLATE_COLUMN_ORDER
    .filter((colKey) => !mappedVisible || mappedVisible.includes(colKey))
    .map((colKey) => ({ label: TEMPLATE_HEADERS_MAP[colKey], key: colKey }));

  const csv = buildCsv(headers, templates);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Caregiver Report Export
//
// One flattened row per caregiver (plan section 8.4), with the reason breakdown
// spread across columns rather than split into a second file.
//
// These two functions are the reference shape for the scheduled variant in
// report_generator.py. Anything changed here has to change there too, or a
// scheduled export and a downloaded one will disagree.
// ---------------------------------------------------------------------------

/**
 * Caregiver report CSV columns — LONG format.
 *
 * One row per caregiver per reason code; a caregiver with no reason breakdown
 * still gets exactly one row, with the four reason columns blank.
 *
 * Long rather than the wide reason-per-column matrix this started as: the column
 * count no longer changes with the data, so the file has a fixed shape whatever
 * range it covers, and it pivots directly.
 *
 * Two distinct reason ids can share a description — a campus code and a cloud
 * code both called "Fall" exist in this data — so those rows are identical apart
 * from their counts. Left that way deliberately: the id is an internal key that
 * means nothing to a reader, and a column of them earns less than it costs.
 */
const CAREGIVER_CSV_COLUMNS = [
  { label: 'Caregiver', key: 'display_name' },
  { label: 'Username', key: 'username' },
  { label: 'Last Login', key: '_last_login' },
  { label: 'Events', key: 'total_events' },
  { label: 'Avg Response (min)', key: '_avg_response' },
  { label: 'Avg Service (min)', key: '_avg_service' },
  { label: 'Measurable Events', key: 'service_time_events' },
  { label: 'No Reason', key: 'no_reason_events' },
  { label: 'Cleared Before Accept', key: 'cleared_before_accepted' },
  { label: 'Reason', key: '_reason' },
  { label: 'Reason Events', key: '_reason_events' },
  { label: 'Reason Avg Service (min)', key: '_reason_avg_service' },
];

/** Last login for export, in CAMPUS-local time, or blank.
 *
 * Both sources are UTC and arrive with no offset, so `new Date(raw)` read them
 * as browser-local and the export disagreed with the campus by the tz offset.
 * Same helper the on-screen table and the other reports use. */
function caregiverLoginCell(raw, tzOffset) {
  if (!raw) return '';
  return convertIsoUTCToLocal(raw, tzOffset ?? 0) || String(raw);
}

/**
 * Caregiver report -> CSV, one row per caregiver per reason.
 *
 * The caregiver-level columns REPEAT on every row of a caregiver's group rather
 * than appearing once and leaving blanks below. Blank-filling looks tidier and
 * breaks everything downstream: filter to a single reason and the totals vanish,
 * sort by any column and the values detach from their rows.
 *
 * The cost of repeating them, worth knowing before anyone sums a column: a
 * caregiver with five reasons contributes their Events figure five times, so
 * SUM(Events) over the whole file overstates the total. Deduplicate on Caregiver,
 * or read the total off the report. This is inherent to the grain, not a defect
 * in the export.
 *
 * @param {Array<object>} records — the report's caregiver rows
 * @param {string} filename
 */
export function exportCaregiverCsv(records, filename = 'caregiver-report.csv', tzOffset = 0) {
  const rows = [];

  (records || []).forEach((rec) => {
    const base = {
      ...rec,
      _last_login: caregiverLoginCell(rec.last_login_time, tzOffset),
      /* Nulls become EMPTY, not the em dash the screen shows. A dash makes Excel
         type the whole column as text, so every downstream AVERAGE and sort
         breaks. */
      _avg_response: rec.avg_response_time ?? '',
      _avg_service: rec.avg_service_time ?? '',
    };

    const reasons = rec.reasons || [];
    if (!reasons.length) {
      /* Still one row. A caregiver who recorded no reason codes — including one
         with no events at all — has to appear, or the export silently drops the
         people a performance report most needs to show. */
      rows.push({ ...base, _reason: '', _reason_events: '', _reason_avg_service: '' });
      return;
    }

    reasons.forEach((rs) => {
      rows.push({
        ...base,
        _reason: rs.reason_code_description || 'No Reason',
        _reason_events: rs.total_events ?? '',
        _reason_avg_service: rs.avg_service_time ?? '',
      });
    });
  });

  /* No Totals row. The table on screen has one because a reader wants it in view;
     a CSV is data, and a totals row breaks sorting, filtering and pivots — doubly
     so at this grain, where it would sit alongside per-reason rows. */
  downloadCsv(buildCsv(CAREGIVER_CSV_COLUMNS, rows), filename);
}

/**
 * Caregiver report -> PDF.
 *
 * Two tables: the caregiver rows, then a flat caregiver-by-reason breakdown. The
 * breakdown is flat rather than nested under each caregiver because autoTable
 * paginates one continuous table cleanly and a per-caregiver sub-table does not.
 *
 * @param {Array<object>} records
 * @param {object} summary — the backend roll-up, already volume-weighted
 * @param {object} meta — { campusName, rangeLabel, generatedAt }
 * @param {string} filename
 */
/**
 * Right-align the HEAD cells of the given column indexes.
 *
 * `columnStyles` in jspdf-autotable reaches body cells but not header cells, so a
 * numeric column ends up with right-aligned figures under a left-aligned heading
 * and the two drift apart across the column's width. Setting halign on the parsed
 * cell is the hook that applies to the header row.
 */
const alignHeadRight = (indexes) => (data) => {
  if (data.section === 'head' && indexes.includes(data.column.index)) {
    data.cell.styles.halign = 'right';
  }
};

export function exportCaregiverPdf(records, summary, meta = {}, filename = 'caregiver-report.pdf', tzOffset = 0) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const s = summary || {};
  const dash = '-';
  const num = (v) => (v === null || v === undefined || v === '' ? dash : String(v));

  doc.setFontSize(14);
  doc.text('Caregiver Report', 14, 15);

  doc.setFontSize(9);
  const subtitle = [meta.campusName, meta.rangeLabel].filter(Boolean).join('   |   ');
  if (subtitle) doc.text(subtitle, 14, 21);

  /* The same figures the Summary tab shows, in the same words. A PDF that
     disagreed with the screen it was exported from would be worse than one with
     no summary at all. */
  const summaryLine = [
    `${(s.total_events || 0).toLocaleString()} events`,
    `${s.reporting_caregivers || 0} of ${s.caregiver_count || 0} caregivers responded`,
    `avg response ${num(s.avg_response_time)} min`,
    `avg service ${num(s.avg_service_time)} min over ${(s.service_time_events || 0).toLocaleString()} measurable`,
    `${(s.no_reason_events || 0).toLocaleString()} no reason`,
    `${(s.cleared_before_accepted || 0).toLocaleString()} cleared before accept`,
  ].join('    ');
  doc.setFontSize(8);
  doc.text(summaryLine, 14, subtitle ? 27 : 21);

  const headStyles = { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' };

  autoTable(doc, {
    head: [['Caregiver', 'Last Login', 'Events', 'Avg Response', 'Avg Service', 'No Reason', 'Cleared Before Accept']],
    body: (records || []).map((r) => ([
      r.display_name || r.username || '',
      caregiverLoginCell(r.last_login_time, tzOffset) || 'Never',
      String(r.total_events ?? 0),
      num(r.avg_response_time),
      num(r.avg_service_time),
      String(r.no_reason_events ?? 0),
      String(r.cleared_before_accepted ?? 0),
    ])),
    startY: subtitle ? 32 : 26,
    styles: { fontSize: 8, cellPadding: 1.6 },
    headStyles,
    alternateRowStyles: { fillColor: [245, 247, 250] },
    columnStyles: {
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
    didParseCell: alignHeadRight([2, 3, 4, 5, 6]),
    margin: { left: 10, right: 10 },
  });

  const breakdown = (records || []).flatMap((r) => (
    (r.reasons || []).map((rs) => ([
      r.display_name || r.username || '',
      rs.reason_code_description || 'No Reason',
      String(rs.total_events ?? 0),
      num(rs.avg_service_time),
    ]))
  ));

  if (breakdown.length) {
    const afterY = doc.lastAutoTable?.finalY || 40;
    doc.setFontSize(11);
    doc.text('Reason code breakdown', 14, afterY + 10);
    autoTable(doc, {
      head: [['Caregiver', 'Reason', 'Events', 'Avg Service']],
      body: breakdown,
      startY: afterY + 14,
      styles: { fontSize: 8, cellPadding: 1.6 },
      headStyles,
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
      didParseCell: alignHeadRight([2, 3]),
      margin: { left: 10, right: 10 },
    });
  }

  /* Footer stamped on every page after both tables exist, so the page count is
     the final one rather than whatever it was mid-build. */
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    const foot = meta.generatedAt
      ? `Generated ${meta.generatedAt}    Page ${i} of ${pages}`
      : `Page ${i} of ${pages}`;
    doc.text(foot, 14, doc.internal.pageSize.getHeight() - 6);
  }

  doc.save(filename);
}

/* --------------------------------------------------------------------------
   Shift Event Breakdown Report
   -------------------------------------------------------------------------- */

/**
 * Columns for the shift CSV, one row per shift per reason.
 *
 * The (s) and (min) suffixes are load-bearing, not tidiness. sp_getShiftReport
 * returns min/p90/max in raw SECONDS and every *_time column in MINUTES, and a
 * file with two duration units and no labels is a wrong chart waiting to happen.
 *
 * Seconds stay seconds here even though the screen renders them as "3m 12s": a
 * CSV is opened to be sorted and averaged, and a duration string can be neither.
 *
 * Mirrors `csvKeys` in report_generator.py's report_type_id 16 branch. A
 * downloaded CSV and a scheduled one must be the same file for the same data; if
 * either side changes, change both.
 */
const SHIFT_CSV_COLUMNS = [
  { label: 'Shift', key: 'shift_name' },
  { label: 'Hours', key: 'shift_hours' },
  { label: 'Events', key: 'total_events' },
  { label: 'Events / Hr', key: '_events_per_hour' },
  { label: 'Events / Day', key: '_events_per_day' },
  { label: 'Avg Response (min)', key: '_avg_response' },
  { label: 'vs Campus (%)', key: '_delta_pct' },
  { label: 'Min Response (s)', key: '_min_seconds' },
  { label: 'p90 Response (s)', key: '_p90_seconds' },
  { label: 'Max Response (s)', key: '_max_seconds' },
  { label: 'Measurable Responses', key: 'response_measurable_events' },
  { label: 'Above Campus Avg', key: 'over_campus_avg_events' },
  { label: 'Avg Service (min)', key: '_avg_service' },
  { label: 'Measurable Service Events', key: 'service_time_events' },
  { label: 'Handoff Events', key: 'handoff_events' },
  { label: 'Shift Change Events', key: 'shift_change_events' },
  { label: 'Reason', key: '_reason' },
  { label: 'Reason Events', key: '_reason_events' },
  { label: 'Reason Avg Service (min)', key: '_reason_avg_service' },
];

/**
 * Shift report -> CSV, one row per shift per reason.
 *
 * The shift-level columns REPEAT on every row of a shift's group rather than
 * appearing once and leaving blanks below. Blank-filling looks tidier and breaks
 * everything downstream: filter to a single reason and the totals vanish, sort by
 * any column and the values detach from their rows.
 *
 * The cost of repeating them, worth knowing before anyone sums a column: a shift
 * with five reasons contributes its Events figure five times, so SUM(Events) over
 * the whole file overstates the total. Deduplicate on Shift, or read the total off
 * the report. Inherent to the grain, not a defect in the export.
 *
 * @param {Array<object>} records — the report's shift rows
 * @param {string} filename
 */
export function exportShiftCsv(records, filename = 'shift-report.csv') {
  const rows = [];

  (records || []).forEach((rec) => {
    const base = {
      ...rec,
      /* Nulls become EMPTY, not the em dash the screen shows. A dash makes Excel
         type the whole column as text, so every downstream AVERAGE and sort
         breaks. */
      _events_per_hour: rec.events_per_shift_hour ?? '',
      _events_per_day: rec.events_per_day ?? '',
      _avg_response: rec.avg_response_time ?? '',
      _delta_pct: rec.response_delta_pct ?? '',
      _min_seconds: rec.min_response_seconds ?? '',
      /* The RAW p90, not the suppressed one. The screen hides it below the
         threshold because a p90 that equals the max invites a bug report; a
         spreadsheet has Measurable Responses in the next column and can make
         that judgement itself. */
      _p90_seconds: rec.p90_response_seconds ?? '',
      _max_seconds: rec.max_response_seconds ?? '',
      _avg_service: rec.avg_service_time ?? '',
    };

    const reasons = rec.reasons || [];
    if (!reasons.length) {
      /* Still one row. A shift that had events but no recorded reasons — or no
         events at all — has to appear, or the export drops exactly the shift a
         reader is most likely to be asking about. */
      rows.push({ ...base, _reason: '', _reason_events: '', _reason_avg_service: '' });
      return;
    }

    reasons.forEach((rs) => {
      rows.push({
        ...base,
        _reason: rs.reason_code_description || 'No Reason',
        _reason_events: rs.total_events ?? '',
        _reason_avg_service: rs.avg_service_time ?? '',
      });
    });
  });

  /* No Totals row. The screen has one because a reader wants it in view; a CSV is
     data, and a totals row breaks sorting, filtering and pivots — doubly so at
     this grain, where it would sit alongside per-reason rows. */
  downloadCsv(buildCsv(SHIFT_CSV_COLUMNS, rows), filename);
}

/** Raw seconds -> "4s" / "3m 12s" / "1h 04m". Local copy of formatDurationSeconds
 *  so this module keeps importing nothing from a page folder. */
function shiftDurationCell(value) {
  if (value === null || value === undefined || value === '') return '-';
  const total = Math.round(Number(value));
  if (!Number.isFinite(total) || total < 0) return '-';
  if (total < 60) return `${total}s`;
  if (total < 3600) return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
  return `${Math.floor(total / 3600)}h ${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}m`;
}

/** 0-23 -> "12 AM" / "7 AM" / "11 PM". */
function shiftHourCell(hour) {
  const h = ((Number(hour) || 0) % 24 + 24) % 24;
  const period = h < 12 ? 'AM' : 'PM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display} ${period}`;
}

/**
 * Shift report -> PDF.
 *
 * Four tables: the shift metrics, the hour-of-day distribution, the handover
 * windows, then the flat shift-by-reason breakdown.
 *
 * ROW PER SHIFT here, not the transposed layout the screen uses. The transpose
 * exists because eleven metrics against three shifts cannot be read as a 3-row
 * table on a screen; a landscape A4 page has the width, and autoTable paginates
 * a row-per-record table cleanly. Same numbers, different orientation.
 *
 * @param {Array<object>} records
 * @param {object} summary — the backend roll-up
 * @param {object} meta — { campusName, rangeLabel, generatedAt, hours, boundaries }
 * @param {string} filename
 */
export function exportShiftPdf(records, summary, meta = {}, filename = 'shift-report.pdf') {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const s = summary || {};
  const dash = '-';
  const num = (v) => (v === null || v === undefined || v === '' ? dash : String(v));
  const pct = (v) => (v === null || v === undefined || v === '' ? dash : `${Number(v).toFixed(1)}%`);
  const signed = (v) => (v === null || v === undefined || v === ''
    ? dash
    : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(1)}%`);

  doc.setFontSize(14);
  doc.text('Shift Event Breakdown', 14, 15);

  doc.setFontSize(9);
  const subtitle = [meta.campusName, meta.rangeLabel].filter(Boolean).join('   |   ');
  if (subtitle) doc.text(subtitle, 14, 21);

  /* The same figures the page shows, in the same words. A PDF that disagreed
     with the screen it was exported from would be worse than one with no summary
     at all. The campus average appears next to the above-average share for the
     reason it does on screen: response times are right-skewed, so that share
     sits near 30-40% and is unreadable without its baseline. */
  const summaryLine = [
    `${(s.total_events || 0).toLocaleString()} events`,
    `${s.shift_count || 0} shifts`,
    `campus avg response ${num(s.campus_avg_response_time)} min`,
    `${pct(s.over_campus_avg_pct)} of events above it`,
    `avg service ${num(s.avg_service_time)} min over ${(s.service_time_events || 0).toLocaleString()} measurable`,
    `${(s.handoff_events || 0).toLocaleString()} handoff-spanning`,
  ].join('    ');
  doc.setFontSize(8);
  doc.text(summaryLine, 14, subtitle ? 27 : 21);

  const headStyles = { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' };
  const baseStyles = { fontSize: 8, cellPadding: 1.6 };
  const zebra = { fillColor: [245, 247, 250] };

  autoTable(doc, {
    head: [['Shift', 'Hours', 'Events', 'Events / Hr', 'Events / Day', 'Avg Response',
      'vs Campus', 'Above Avg', 'p90', 'Max', 'Min', 'Avg Service', 'Handoff']],
    body: (records || []).map((r) => ([
      r.shift_name || '',
      String(r.shift_hours ?? 0),
      String(r.total_events ?? 0),
      num(r.events_per_shift_hour),
      num(r.events_per_day),
      num(r.avg_response_time),
      signed(r.response_delta_pct),
      pct(r.over_campus_avg_pct),
      r.p90_suppressed ? dash : shiftDurationCell(r.p90_response_seconds),
      shiftDurationCell(r.max_response_seconds),
      shiftDurationCell(r.min_response_seconds),
      num(r.avg_service_time),
      String(r.handoff_events ?? 0),
    ])),
    startY: subtitle ? 32 : 26,
    styles: baseStyles,
    headStyles,
    alternateRowStyles: zebra,
    /* Every numeric column right-aligned in the BODY via columnStyles, and in
       the HEAD via didParseCell — columnStyles does not reach header cells, so
       without the hook the figures right-align under left-aligned headings and
       the two drift apart across the column's width. */
    columnStyles: {
      1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
      4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
      7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' },
      10: { halign: 'right' }, 11: { halign: 'right' }, 12: { halign: 'right' },
    },
    didParseCell: alignHeadRight([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    margin: { left: 10, right: 10 },
  });

  const hours = meta.hours || [];
  if (hours.length) {
    const afterY = doc.lastAutoTable?.finalY || 40;
    doc.setFontSize(11);
    doc.text('By hour of day', 14, afterY + 10);
    autoTable(doc, {
      head: [['Hour', 'Shift', 'Events', 'Avg Response (min)']],
      body: hours.map((h) => ([
        shiftHourCell(h.local_hour),
        h.shift_name || '',
        String(h.total_events ?? 0),
        num(h.avg_response_time),
      ])),
      startY: afterY + 14,
      styles: baseStyles,
      headStyles,
      alternateRowStyles: zebra,
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
      didParseCell: alignHeadRight([2, 3]),
      margin: { left: 10, right: 10 },
    });
  }

  const boundaries = meta.boundaries || [];
  if (boundaries.length) {
    const afterY = doc.lastAutoTable?.finalY || 40;
    doc.setFontSize(11);
    doc.text('Shift change windows', 14, afterY + 10);
    autoTable(doc, {
      head: [['Handover', 'At', 'Events', 'Avg Response (min)']],
      body: boundaries.map((b) => ([
        `${b.from_shift || '?'} -> ${b.to_shift || '?'}`,
        shiftHourCell(b.boundary_hour),
        String(b.total_events ?? 0),
        num(b.avg_response_time),
      ])),
      startY: afterY + 14,
      styles: baseStyles,
      headStyles,
      alternateRowStyles: zebra,
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
      didParseCell: alignHeadRight([2, 3]),
      margin: { left: 10, right: 10 },
    });
  }

  /* Flat shift-by-reason rows, sentinels included so each shift's rows total to
     its Events figure in the first table. */
  const breakdown = (records || []).flatMap((r) => (
    (r.reasons || []).map((rs) => ([
      r.shift_name || '',
      rs.reason_code_description || 'No Reason',
      String(rs.total_events ?? 0),
      rs.reason_id === -1 || rs.reason_id === -2 ? 'n/a' : num(rs.avg_service_time),
    ]))
  ));

  if (breakdown.length) {
    const afterY = doc.lastAutoTable?.finalY || 40;
    doc.setFontSize(11);
    doc.text('Reason code breakdown', 14, afterY + 10);
    doc.setFontSize(8);
    doc.text('Includes automatically closed alerts, so each shift totals to its Events figure above.',
      14, afterY + 15);
    autoTable(doc, {
      head: [['Shift', 'Reason', 'Events', 'Avg Service (min)']],
      body: breakdown,
      startY: afterY + 19,
      styles: baseStyles,
      headStyles,
      alternateRowStyles: zebra,
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
      didParseCell: alignHeadRight([2, 3]),
      margin: { left: 10, right: 10 },
    });
  }

  /* Footer stamped on every page after every table exists, so the page count is
     the final one rather than whatever it was mid-build. */
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    const foot = meta.generatedAt
      ? `Generated ${meta.generatedAt}    Page ${i} of ${pages}`
      : `Page ${i} of ${pages}`;
    doc.text(foot, 14, doc.internal.pageSize.getHeight() - 6);
  }

  doc.save(filename);
}
